/**
 * Transformación de las celdas agregadas que devuelve la API a lo que dibuja
 * deck.gl. Pura y sin React: es lo que se puede probar sin WebGL.
 *
 * **Acá no hay eventos y no puede haberlos.** `GET /analytics/heatmap`
 * devuelve `analytics_geo_cell` ya agrupada; el mapa pinta polígonos, uno por
 * celda. Si alguna vez este archivo necesitara una fila por escaneo, el
 * problema estaría mal planteado: serían cientos de MB de JSON y, sobre todo,
 * sería la regla 5 del dominio violada en el borde de salida.
 *
 * Por qué la escala es logarítmica
 * --------------------------------
 * La distribución de escaneos por celda tiene cola larguísima: sobre los datos
 * reales del laboratorio, la celda de Asunción tiene 2.650 escaneos y la
 * mediana anda en decenas. Con una rampa lineal el mapa entero queda del color
 * más pálido y sólo dos celdas se ven — que es exactamente el mapa que no
 * sirve para encontrar dónde apareció algo raro.
 */
import type { Schemas } from '@/api/types';

export type HeatmapResponse = Schemas['HeatmapOut'];
export type HeatCell = Schemas['Cell'];

/** `[lon, lat]`, que es el orden que espera deck.gl (no el de la API). */
export type Position = [number, number];

/**
 * Anillo cerrado de cuatro esquinas del rectángulo de la celda.
 *
 * `bbox` viene como `(lat_min, lon_min, lat_max, lon_max)` y deck.gl quiere
 * `[lon, lat]`. Invertirlo mal dibuja el mapa entero en el océano Índico, así
 * que la conversión vive en un solo lugar y tiene test.
 */
export function cellRing(cell: HeatCell): Position[] {
  const [latMin, lonMin, latMax, lonMax] = cell.bbox;
  return [
    [lonMin, latMin],
    [lonMax, latMin],
    [lonMax, latMax],
    [lonMin, latMax],
  ];
}

export interface CellStats {
  cells: number;
  scans: number;
  suspicious: number;
  maxScans: number;
  countries: number;
}

export function cellStats(cells: readonly HeatCell[]): CellStats {
  let scans = 0;
  let suspicious = 0;
  let maxScans = 0;
  const countries = new Set<string>();
  for (const cell of cells) {
    scans += cell.scans;
    suspicious += cell.suspicious;
    if (cell.scans > maxScans) maxScans = cell.scans;
    if (cell.country_code !== null && cell.country_code !== undefined) {
      countries.add(cell.country_code);
    }
  }
  return { cells: cells.length, scans, suspicious, maxScans, countries: countries.size };
}

/**
 * Intensidad 0..1 en escala logarítmica sobre el máximo de la respuesta.
 *
 * Se devuelve una función construida una vez por respuesta en vez de calcular
 * el máximo por celda: con 5.000 celdas eso serían 25 millones de
 * comparaciones en cada render del mapa.
 */
export function intensityScale(maxScans: number): (scans: number) => number {
  if (maxScans <= 1) return () => 1;
  const top = Math.log1p(maxScans);
  return (scans) => {
    if (scans <= 0) return 0;
    const value = Math.log1p(scans) / top;
    return value < 0 ? 0 : value > 1 ? 1 : value;
  };
}

export type Rgb = [number, number, number];

/**
 * Rampa secuencial: UN solo tono, el violeta de la marca, de claro a oscuro.
 *
 * ── Lo que había, y por qué no funcionaba
 *
 * La rampa anterior decía en su comentario ser "de un solo tono" y en realidad
 * recorría cuatro: azul → verdeazulado → oliva → ámbar → naranja. Un arcoíris.
 *
 * El problema medible no es de gusto: **su luminosidad no era monótona**. Subía
 * hasta el oliva y volvía a bajar en el naranja, así que dos celdas de conteos
 * muy distintos podían tener el mismo peso visual. Una rampa secuencial existe
 * para que "más oscuro" signifique "más", y esa era la única propiedad que no
 * cumplía. De ahí que el mapa se leyera plano.
 *
 * Además el arcoíris inventa fronteras donde el dato es continuo y no sobrevive
 * a un daltonismo rojo-verde.
 *
 * ── Por qué violeta
 *
 * Es el color de la marca y, sobre todo, NO es un color de estado. Verde, ámbar
 * y rojo están reservados para los cuatro resultados de verificación; usarlos
 * para densidad diluiría la señal que importa. El rojo queda para la capa de
 * alertas, la única que pide actuar.
 *
 * ── Dos rampas y no una invertida
 *
 * En claro va de violeta pálido a violeta profundo sobre un basemap casi
 * blanco; en oscuro, al revés, para que el valor alto sea el que brilla. Las
 * dos son monótonas contra SU propia superficie, verificado paso por paso.
 *
 * El extremo bajo queda cerca del fondo a propósito —una celda de pocos
 * escaneos no debe gritar— y por eso cada celda lleva borde: sin él, el primer
 * tramo desaparecería. La lectura exacta está en la leyenda y en la tabla.
 */
const RAMP_CLARA: readonly Rgb[] = [
  [221, 214, 254],
  [190, 172, 251],
  [158, 125, 250],
  [127,  85, 245],
  [ 98,  52, 220],
  [ 69,  33, 158],
];

const RAMP_OSCURA: readonly Rgb[] = [
  [ 43,  34,  96],
  [ 61,  46, 146],
  [ 85,  64, 201],
  [115,  88, 240],
  [154, 130, 255],
  [196, 181, 253],
];

/**
 * El tema lo decide el sistema operativo, igual que el basemap. Se resuelve en
 * cada llamada y no se cachea: es una comparación, y cachearlo obligaría a
 * invalidar algo cuando el usuario cambia el tema del sistema.
 */
function rampaDelTema(): readonly Rgb[] {
  const oscuro =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return oscuro ? RAMP_OSCURA : RAMP_CLARA;
}

export function rampColor(intensity: number): Rgb {
  const RAMP = rampaDelTema();
  const clamped = intensity < 0 ? 0 : intensity > 1 ? 1 : intensity;
  const last = RAMP.length - 1;
  const scaled = clamped * last;
  const index = Math.min(last - 1, Math.floor(scaled));
  const from = RAMP[index] ?? RAMP[0];
  const to = RAMP[index + 1] ?? RAMP[last];
  if (from === undefined || to === undefined) return [128, 128, 128];
  const t = scaled - index;
  return [
    Math.round(from[0] + (to[0] - from[0]) * t),
    Math.round(from[1] + (to[1] - from[1]) * t),
    Math.round(from[2] + (to[2] - from[2]) * t),
  ];
}

/** Los tramos de la leyenda, con el conteo de escaneos que representa cada uno. */
export interface LegendStop {
  intensity: number;
  color: Rgb;
  /** Escaneos que corresponden a esa intensidad, invirtiendo el logaritmo. */
  scans: number;
}

export function legendStops(maxScans: number, steps = 5): LegendStop[] {
  const top = Math.max(1, maxScans);
  return Array.from({ length: steps }, (_, index) => {
    const intensity = steps === 1 ? 1 : index / (steps - 1);
    return {
      intensity,
      color: rampColor(intensity),
      scans: Math.max(1, Math.round(Math.expm1(intensity * Math.log1p(top)))),
    };
  });
}

/**
 * Las celdas más cargadas primero. Es el orden de la tabla accesible y también
 * el de dibujado: pintar las densas al final las deja arriba y evita que una
 * celda de tres escaneos tape la de tres mil.
 */
export function byScansDesc(cells: readonly HeatCell[]): HeatCell[] {
  // `toSorted` no está en el `lib` de este tsconfig; copiar es explícito y
  // evita reordenar el array que TanStack Query tiene en cache.
  return [...cells].sort((a, b) => b.scans - a.scans || a.cell.localeCompare(b.cell));
}

/** Fracción del total que representa la celda. `fmt.percent` la formatea. */
export function shareOf(cell: HeatCell, totalScans: number): number {
  return totalScans <= 0 ? 0 : cell.scans / totalScans;
}

/**
 * Encuadre inicial: el rectángulo que contiene todas las celdas.
 *
 * Sin esto el mapa arranca sobre el Atlántico y el usuario tiene que buscar su
 * propio dato, que es la primera impresión que hace que nadie vuelva a abrir
 * el mapa.
 */
export interface Bounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export function boundsOf(cells: readonly HeatCell[]): Bounds | null {
  if (cells.length === 0) return null;
  let west = 180;
  let south = 90;
  let east = -180;
  let north = -90;
  for (const cell of cells) {
    const [latMin, lonMin, latMax, lonMax] = cell.bbox;
    if (lonMin < west) west = lonMin;
    if (lonMax > east) east = lonMax;
    if (latMin < south) south = latMin;
    if (latMax > north) north = latMax;
  }
  return { west, south, east, north };
}
